// ============================================================================
// anatomyRegistry.js — the single source of truth for searchable anatomy.
//
// Phase 1 (now): base data is sourced from the per-system *_NAMES maps so the
// search bar reads from one place. EXTRAS layer adds camera framing overrides
// and reveal flags. Per-system model components still use their own data files
// unchanged; they will be migrated to read from here later.
//
// Each registry entry:
//   {
//     system, english, latin, side,
//     camera: { target:[x,y,z], orbit:"thetaDeg phiDeg", distance },  // optional
//     reveal: 'split' | 'peel',                                       // optional
//   }
// ============================================================================

import { BONE_NAMES } from './boneNames';
import { BONE_DATA } from './boneData';
import { SYSTEM_POSITIONS } from './systemPositions';
import { BRAIN_NAMES, SPLIT_DISTANCE } from './brainData';
import { CARDIO_NAMES } from './cardioData';
import { DIGESTIVE_NAMES } from './digestiveData';
import { LYMPH_NAMES } from './lymphData';
import { MUSCLE_NAMES } from './muscleNames';
import { NERVE_NAMES } from './nerveData';
import { RESPIRATORY_NAMES } from './respiratoryData';
import { URINARY_NAMES } from './urinaryData';

const SYSTEMS = [
  { id: 'skeleton', label: 'Skeleton', icon: 'https://cdn-icons-png.flaticon.com/512/7437/7437376.png', names: BONE_NAMES },
  { id: 'brain', label: 'Brain', icon: 'https://cdn-icons-png.flaticon.com/512/6969/6969710.png', names: BRAIN_NAMES },
  { id: 'muscles', label: 'Muscles', icon: 'https://cdn-icons-png.flaticon.com/512/7671/7671649.png', names: MUSCLE_NAMES },
  { id: 'cardiovascularsystem', label: 'Cardiovascular System', icon: 'https://cdn-icons-png.flaticon.com/512/14323/14323111.png', names: CARDIO_NAMES },
  { id: 'nervoussystem', label: 'Nervous System', icon: 'https://cdn-icons-png.flaticon.com/512/11603/11603901.png', names: NERVE_NAMES },
  { id: 'lymphaticsystem', label: 'Lymphatic System', icon: 'https://cdn-icons-png.flaticon.com/512/3323/3323127.png', names: LYMPH_NAMES },
  { id: 'digestivesystem', label: 'Digestive System', icon: 'https://cdn-icons-png.flaticon.com/512/10172/10172608.png', names: DIGESTIVE_NAMES },
  { id: 'respiratorysystem', label: 'Respiratory System', icon: 'https://cdn-icons-png.flaticon.com/512/9774/9774039.png', names: RESPIRATORY_NAMES },
  { id: 'urinarysystem', label: 'Urinary System', icon: 'https://cdn-icons-png.flaticon.com/512/17306/17306383.png', names: URINARY_NAMES },
];

// Brain structures that sit medial/interior — only visible once the hemispheres
// are split. Surface parts (cerebrum, cerebellum, brainstem) are NOT listed.
const BRAIN_INTERIOR_BASES = [
  'Amygdala',
  'Caudate nucleus',
  'Cingulate gyrus',
  'Corpus callosum',
  'Dentate gyrus',
  'Fornix',
  'Globus pallidus lateral',
  'Globus pallidus medial',
  'Hippocampus',
  'Indusium griseum',
  'Lateral ventricle',
  'Medulla oblongata',
  'Midbrain',
  'Pons',
  'Putamen',
  'Septal nucleus',
  'Thalamus',
];

function detectSide(text) {
  const t = (text || '').toLowerCase();
  const left = /\bleft\b|\(left\)|sinister|sinistra|sinistrum/.test(t);
  const right = /\bright\b|\(right\)|dexter|dextra|dextrum/.test(t);
  if (left && !right) return 'left';
  if (right && !left) return 'right';
  return null;
}

function baseNameOf(english) {
  return (english || '')
    .replace(/\s*[\u2014\u2013-]+\s*(Left|Right)\s*$/i, '')
    .replace(/\s*[(/]?\s*(Left|Right)\s*[)]?/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalize(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[\u2014\u2013]/g, ' ')
    .replace(/[_\-.]+/g, ' ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// ---------------------------------------------------------------------------
// Normalized position lookup — handles name mismatches between dictionary keys
// (underscores) and GLB mesh names (spaces). Strips all non-alphanumeric chars
// and compares case-insensitively.
// ---------------------------------------------------------------------------
const NORMALIZED_POSITION_MAPS = {};
for (const [systemId, positions] of Object.entries(SYSTEM_POSITIONS)) {
  const map = {};
  for (const [name, pos] of Object.entries(positions)) {
    map[name.toLowerCase().replace(/[^a-z0-9]/gi, '')] = pos;
  }
  NORMALIZED_POSITION_MAPS[systemId] = map;
}

function findPosition(systemId, meshKey) {
  const map = NORMALIZED_POSITION_MAPS[systemId];
  if (!map) return null;
  return map[meshKey.toLowerCase().replace(/[^a-z0-9]/gi, '')] || null;
}

// ---------------------------------------------------------------------------
// CAMERA_EXTRAS — per-mesh camera framing overrides.
//
// The auto-framing engine picks a viewing angle from the bounding box's
// thinnest axis (thinnestAxisOrbit) then mirrors it for right-side parts.
// That heuristic is wrong for two families:
//   1. Skeleton: Left = -X / Right = +X (OPPOSITE of the brain model), so the
//      right-side mirror lands the camera on the wrong side for every flat,
//      side-viewed (X-thinnest) bilateral bone.
//   2. Top-down default: thinnestAxisOrbit returns phi:15 (top view) when Y is
//      the thinnest dimension, which is wrong for parts that read better from
//      the front or back (occipital, spine, mandible, etc.).
//
// Each entry merges into entry.camera in buildRegistry(). Orbit conventions
// (verified: sternum/linea alba sit at +Z = front, spine/occipital at -Z):
//   front       = "0deg 80deg"     camera at +Z (anterior)
//   back        = "180deg 80deg"   camera at -Z (posterior)
//   sidePlusX   = "80deg 75deg"    camera approaches from +X
//   sideMinusX  = "-80deg 75deg"   camera approaches from -X
//   side        = "90deg 80deg"    generic lateral (midline structures)
// Skeleton bilateral parts: Left = -X -> sideMinusX, Right = +X -> sidePlusX.
// Nervous/urinary bilateral: Left = +X -> sidePlusX, Right = -X -> sideMinusX.
// ---------------------------------------------------------------------------
const ORBIT = {
  front: '0deg 80deg',
  back: '180deg 80deg',
  sidePlusX: '80deg 75deg',
  sideMinusX: '-80deg 75deg',
  side: '90deg 80deg',
};

const CAMERA_EXTRAS = {
  // ===== SKELETON: left/right camera orbit switched (flat side-viewed bones) =====
  "Parietal_left_skull": { orbit: ORBIT.sideMinusX },
  "Parietal_right_skull": { orbit: ORBIT.sidePlusX },
  "Maxilla_left_skull": { orbit: ORBIT.sideMinusX },
  "Maxilla_right_skull": { orbit: ORBIT.sidePlusX },
  "Zygomatic_left_skull": { orbit: ORBIT.sideMinusX },
  "Zygomatic_right_skull": { orbit: ORBIT.sidePlusX },
  "Lacrimal_001left_skull": { orbit: ORBIT.sideMinusX },
  "Lacrimal_right_skull": { orbit: ORBIT.sidePlusX },
  "Lateral cartilage_left_skull": { orbit: ORBIT.sideMinusX },
  "Lateral cartilage_right_skull": { orbit: ORBIT.sidePlusX },
  "Joint capsule_left_skull": { orbit: ORBIT.sideMinusX },
  "Joint capsule_right_skull": { orbit: ORBIT.sidePlusX },
  "Lateral ligament_left_skull": { orbit: ORBIT.sideMinusX },
  "Lateral ligament_right_skull": { orbit: ORBIT.sidePlusX },
  "Sphenomandibular ligament_left_skull": { orbit: ORBIT.sideMinusX },
  "Sphenomandibular ligament_right_skull": { orbit: ORBIT.sidePlusX },
  "Stylomandibular ligament_left_skull": { orbit: ORBIT.sideMinusX },
  "Stylomandibular ligament_right_skull": { orbit: ORBIT.sidePlusX },

  // ===== SKELETON: wrong viewing angle — should load from behind =====
  "Occipital_skull": { orbit: ORBIT.back },
  "Posterior atlanto occipital membrane": { orbit: ORBIT.back },
  "Ligamenta flava": { orbit: ORBIT.back },
  "Rectovaginal fascia_001": { orbit: ORBIT.back },
  // elbow ulnar collateral ligaments
  "Ulnar collateral_L": { orbit: ORBIT.back },
  "Ulnar collateral_R": { orbit: ORBIT.back },
  "Ulnar collateral ligament 01_L": { orbit: ORBIT.back },
  "Ulnar collateral ligament 02_L": { orbit: ORBIT.back },
  "Ulnar collateral ligament _R": { orbit: ORBIT.back },
  "Ulnar collateral ligament _R_001": { orbit: ORBIT.back },
  // pelvis / hip / knee / ankle ligaments (posterior approach)
  "Sacrotuberous ligament_L": { orbit: ORBIT.back },
  "Sacrotuberous ligament_R_001": { orbit: ORBIT.back },
  "Ischiofemoral ligament_L_001": { orbit: ORBIT.back },
  "Ischiofemoral ligament_R_001": { orbit: ORBIT.back },
  "Anterior cruciate ligament_L": { orbit: ORBIT.back },
  "Anterior cruciate ligament_R": { orbit: ORBIT.back },
  "Posterior cruciate ligament_L": { orbit: ORBIT.back },
  "Posterior cruciate ligament_R": { orbit: ORBIT.back },
  "Medial deltoid ligament_L": { orbit: ORBIT.back },
  "Medial deltoid ligament_R": { orbit: ORBIT.back },
  "Medial deltoid ligament _L_001": { orbit: ORBIT.back },
  "Medial deltoid ligament _R": { orbit: ORBIT.back },
  "posterior talocacaneal ligament_L": { orbit: ORBIT.back },
  "posterior talocacaneal ligament_R": { orbit: ORBIT.back },

  // ===== SKELETON: should load from the front (currently top/side) =====
  "Mandibula_skull": { orbit: ORBIT.front },
  "Mandible_skull": { orbit: ORBIT.front },
  "mandibula_skull": { orbit: ORBIT.front },
  "mandible_skull": { orbit: ORBIT.front },
  "Posterior longitudinal ligament": { orbit: ORBIT.front },
  "Anterior longitudinal ligament": { orbit: ORBIT.front },
  "Body of sternum": { orbit: ORBIT.front },
  "Linea alba": { orbit: ORBIT.front },

  // ===== NERVOUS SYSTEM: medulla oblongata L/R orbit swapped =====
  // Nervous convention Left = +X (same as brain), so _L -> sidePlusX.
  "Medulla oblongata_L": { orbit: ORBIT.sidePlusX },
  "Medulla oblongata_R": { orbit: ORBIT.sideMinusX },

  // ===== DIGESTIVE SYSTEM: internal anal sphincter (top -> side) =====
  "Anal_sphincter_internal_001": { orbit: ORBIT.side },

  // ===== RESPIRATORY SYSTEM: larynx cartilages (top -> behind) =====
  "Arythenoid_cartilage": { orbit: ORBIT.back },
  "Corniculate_cartilage": { orbit: ORBIT.back },
  "Cricoid_cartilage": { orbit: ORBIT.back },
  "Cuneiform_cartilage": { orbit: ORBIT.back },

  // ===== URINARY SYSTEM =====
  // Kidney posterior surface: load from behind (currently front).
  "Kidney Back L": { orbit: ORBIT.back },
  "Kidney Back R": { orbit: ORBIT.back },
  "Kidney_Back_L": { orbit: ORBIT.back },
  "Kidney_Back_R": { orbit: ORBIT.back },
  // Bladder L/R: distinct lateral orbits (Urinary Left = +X).
  "Bladder_L": { orbit: ORBIT.sidePlusX },
  "Bladder_R": { orbit: ORBIT.sideMinusX },
};

// Spinal vertebrae C1-L5: focused correctly but load top-down — switch to back.
(function () {
  const spine = [];
  spine.push('C01 Atlas', 'C02 Axis');
  for (let i = 3; i <= 7; i++) spine.push(`C0${i} Vertebra`);
  for (let i = 1; i <= 12; i++) spine.push(`T${String(i).padStart(2, '0')} Vertebra`);
  for (let i = 1; i <= 5; i++) spine.push(`L0${i} Vertebra`);
  for (const v of spine) CAMERA_EXTRAS[v] = { orbit: ORBIT.back };
})();

// ---------------------------------------------------------------------------
// Build the mesh-keyed registry with EXTRAS (camera overrides + reveal flags).
// ---------------------------------------------------------------------------
function buildRegistry() {
  const reg = {};

  for (const sys of SYSTEMS) {
    for (const [meshKey, info] of Object.entries(sys.names)) {
      // Keep the first occurrence per meshKey (avoids alias overwrites).
      if (reg[meshKey]) continue;

      const english = info.english || meshKey;
      const latin = info.latin || '';
      const side = detectSide(english) || detectSide(latin);

      const entry = {
        system: sys.id,
        english,
        latin,
        side,
      };

      // Reveal flags (must be set before camera target logic).
      if (sys.id === 'brain') {
        const base = meshKey.replace(/_L$/, '').replace(/_R$/, '');
        if (BRAIN_INTERIOR_BASES.includes(base)) entry.reveal = 'split';
      }
      if (sys.id === 'muscles') entry.reveal = 'peel';

      // Camera framing for reveal:'split' (brain interior).
      // The hemispheres separate along X (LEFT -> +X, RIGHT -> -X), so the
      // medial faces — where the hippocampus/thalamus/etc. live — are exposed
      // facing each other across the gap. The viewing angle is computed by
      // cameraFrame.brainSideOrbit() from `entry.side`: a TOP-INTO-GAP angle
      // (low phi) so the opposite hemisphere never occludes the target.
      //
      // The target/anchor is pre-computed DETERMINISTICALLY from the mesh's
      // vertex-centroid position + the split offset, rather than measured from
      // the (possibly stale) post-split world matrix. This guarantees both the
      // camera target and the label anchor land on the mesh's true final
      // position, not its pre-split local spot. Geometry in Brain.glb is baked,
      // so post-split world = centroid + (sign * SPLIT_DISTANCE, 0, 0).
      if (entry.reveal === 'split') {
        const pos = findPosition(sys.id, meshKey);
        if (pos) {
          const dx = entry.side === 'left' ? SPLIT_DISTANCE
            : entry.side === 'right' ? -SPLIT_DISTANCE : 0;
          entry.camera = {
            target: [pos[0] + dx, pos[1], pos[2]],
            distance: 0.35,
          };
        } else {
          entry.camera = { distance: 0.35 };
        }
      } else {
        let pos = null;
        if (sys.id === 'skeleton') {
          const boneDatum = BONE_DATA[meshKey];
          if (boneDatum && Array.isArray(boneDatum.position)) pos = boneDatum.position;
        } else {
          pos = findPosition(sys.id, meshKey);
        }
        if (pos) entry.camera = { target: pos.slice() };
      }

      const extra = CAMERA_EXTRAS[meshKey];
      if (extra) {
        entry.camera = { ...(entry.camera || {}), ...extra };
      }

      reg[meshKey] = entry;
    }
  }

  return reg;
}

export const REGISTRY = buildRegistry();

export function getEntry(meshKey) {
  if (!meshKey) return null;
  return REGISTRY[meshKey] || null;
}

// ---------------------------------------------------------------------------
// Search index (flat + deduped + grouped). Behaviour preserved verbatim from
// the previous searchIndex.js; only the data source moved.
// ---------------------------------------------------------------------------
const FLAT_INDEX = [];
for (const sys of SYSTEMS) {
  for (const [meshKey, info] of Object.entries(sys.names)) {
    const english = info.english || meshKey;
    const latin = info.latin || '';
    const side = detectSide(english) || detectSide(latin);
    FLAT_INDEX.push({
      systemId: sys.id,
      systemLabel: sys.label,
      systemIcon: sys.icon,
      meshKey,
      english,
      latin,
      side,
      baseName: baseNameOf(english),
      searchText: normalize(`${english} ${latin} ${meshKey}`),
    });
  }
}

const DEDUPED_INDEX = (() => {
  const seen = new Set();
  const out = [];
  for (const e of FLAT_INDEX) {
    const k = `${e.systemId}||${(e.english || '').toLowerCase()}||${e.side || ''}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(e);
  }
  return out;
})();

export function searchMeshes(query, limit = 40) {
  const q = normalize(query);
  if (!q) return [];
  const terms = q.split(' ').filter(Boolean);

  const scored = [];
  for (const entry of DEDUPED_INDEX) {
    const hay = entry.searchText;
    if (!hay) continue;
    let allMatch = true;
    let score = 0;
    for (const t of terms) {
      const idx = hay.indexOf(t);
      if (idx === -1) { allMatch = false; break; }
      score += idx === 0 ? 12 : 6;
    }
    if (!allMatch) continue;
    if (hay.startsWith(q)) score += 60;
    if (normalize(entry.baseName).startsWith(q)) score += 40;
    if (entry.baseName.toLowerCase() === q) score += 25;
    scored.push({ entry, score });
  }
  scored.sort((a, b) => b.score - a.score || a.entry.systemLabel.localeCompare(b.entry.systemLabel));

  const groups = new Map();
  for (const { entry } of scored.slice(0, limit)) {
    const key = `${entry.systemId}||${entry.baseName.toLowerCase()}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        systemId: entry.systemId,
        systemLabel: entry.systemLabel,
        systemIcon: entry.systemIcon,
        baseName: entry.baseName,
        sides: [],
      };
      groups.set(key, g);
    }
    const sideTag = entry.side || 'center';
    if (!g.sides.find((s) => s.side === sideTag)) {
      g.sides.push({
        side: entry.side,
        meshKey: entry.meshKey,
        english: entry.english,
        latin: entry.latin,
      });
    }
  }

  const list = Array.from(groups.values());
  list.sort((a, b) => {
    const aHas = a.sides.find((s) => normalize(`${a.baseName}`).includes(q)) ? 1 : 0;
    const bHas = b.sides.find((s) => normalize(`${b.baseName}`).includes(q)) ? 1 : 0;
    return bHas - aHas || a.systemLabel.localeCompare(b.systemLabel) || a.baseName.localeCompare(b.baseName);
  });
  return list;
}
